import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { Search, MapPin, Home, Users, TrendingUp, ArrowRight, Phone, Mail } from 'lucide-react';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import { fixImageUrl } from '../lib/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const [listings, setListings] = useState([]);
  const [searchCity, setSearchCity] = useState('');
  const [siteSettings, setSiteSettings] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchListings();
    fetchSiteSettings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/listings/public`);
      setListings(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/`);
      setSiteSettings(response.data || {});
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/properties?city=${encodeURIComponent(searchCity)}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé Properties" className="h-10 w-10 rounded-full object-cover" />
            <span className="font-heading text-xl font-bold tracking-tight text-brand-brown">Signé <span className="text-brand-gold">Properties</span></span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/properties" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-properties">
              Properties
            </Link>
            <a href="#about" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors">
              About
            </a>
            <a href="#contact" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors">
              Contact
            </a>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button 
                onClick={() => navigate(user.role === 'admin' ? '/admin' : '/employee')} 
                className="rounded-none font-heading font-bold uppercase tracking-wide bg-black text-white hover:bg-black/80"
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <Link to="/login" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="login-btn">
                Staff Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 min-h-screen grid grid-cols-1 lg:grid-cols-12 gap-0">
        <div className="lg:col-span-7 relative h-[50vh] lg:h-screen overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1766603636562-531bb3e1dda8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MjcyOTIxM3ww&ixlib=rb-4.1.0&q=85"
            alt="Modern Luxury Home"
            className="w-full h-full object-cover listing-image"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        <div className="lg:col-span-5 flex flex-col justify-end p-8 lg:p-16 bg-white">
          <div className="space-y-8">
            <Badge className="rounded-none px-3 py-1 font-heading text-[10px] uppercase tracking-widest font-bold bg-brand-gold text-brand-brown">
              Premium Properties
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-tight">
              Find Your<br/>
              <span className="text-brand-gold">Dream Home</span>
            </h1>
            <p className="font-body text-base text-muted-foreground leading-relaxed max-w-md">
              Discover exceptional properties curated by our expert team. 
              From modern penthouses to classic family homes, we bring your vision to life.
            </p>
            <form onSubmit={handleSearch} className="flex gap-0 max-w-md">
              <div className="relative flex-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input 
                  placeholder="Enter city or location"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="rounded-none border-2 border-brand-brown pl-10 pr-4 py-6 font-body"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="rounded-none font-heading font-bold uppercase tracking-wide px-8 py-6 bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                data-testid="search-btn"
              >
                <Search className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-brand-brown text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="font-heading text-4xl md:text-5xl font-extrabold text-brand-gold">500+</div>
              <div className="font-body text-sm text-white/60 uppercase tracking-widest">Properties Sold</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="font-heading text-4xl md:text-5xl font-extrabold text-brand-gold">98%</div>
              <div className="font-body text-sm text-white/60 uppercase tracking-widest">Client Satisfaction</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="font-heading text-4xl md:text-5xl font-extrabold text-brand-gold">15+</div>
              <div className="font-body text-sm text-white/60 uppercase tracking-widest">Years Experience</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="font-heading text-4xl md:text-5xl font-extrabold text-brand-gold">50+</div>
              <div className="font-body text-sm text-white/60 uppercase tracking-widest">Expert Agents</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="space-y-4">
              <p className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">Featured</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Latest Properties
              </h2>
            </div>
            <Link to="/properties">
              <Button variant="outline" className="rounded-none border-2 border-brand-brown text-brand-brown hover:bg-brand-brown hover:text-white font-heading uppercase tracking-wide" data-testid="view-all-btn">
                View All Properties
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing, index) => (
                <Link 
                  key={listing.id} 
                  to={`/property/${listing.id}`}
                  className="group block"
                  data-testid={`listing-card-${index}`}
                >
                  <div className="bg-white shadow-sm overflow-hidden hover-lift">
                    <div className="relative h-64 overflow-hidden">
                      {listing.images && listing.images.length > 0 ? (
                        <img 
                          src={fixImageUrl(listing.images[0])} 
                          alt={listing.title}
                          className="w-full h-full object-cover listing-image"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <Home className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <Badge 
                        className={`absolute top-4 left-4 rounded-none px-3 py-1 font-heading text-[10px] uppercase tracking-widest font-bold ${
                          listing.status === 'sold' 
                            ? 'bg-brand-gold text-brand-brown' 
                            : listing.status === 'pending'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-brand-brown text-white'
                        }`}
                      >
                        {listing.status}
                      </Badge>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span className="font-body text-sm">{listing.city || listing.address}</span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-foreground group-hover:text-brand-gold transition-colors line-clamp-1">
                        {listing.title}
                      </h3>
                      <p className="font-heading text-xl font-bold text-brand-gold">
                        {formatPrice(listing.price)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="font-body text-muted-foreground">No properties available yet</p>
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <p className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">About Us</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Your Trusted Partner in Real Estate
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed">
                With over 15 years of experience in the real estate market, we've helped thousands of 
                families find their perfect home. Our team of dedicated professionals is committed to 
                providing exceptional service and expertise.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Home className="w-8 h-8 text-brand-gold" />
                  <h3 className="font-heading text-lg font-semibold">Premium Listings</h3>
                  <p className="font-body text-sm text-muted-foreground">Curated selection of quality properties</p>
                </div>
                <div className="space-y-2">
                  <Users className="w-8 h-8 text-brand-gold" />
                  <h3 className="font-heading text-lg font-semibold">Expert Agents</h3>
                  <p className="font-body text-sm text-muted-foreground">Professional guidance every step</p>
                </div>
                <div className="space-y-2">
                  <TrendingUp className="w-8 h-8 text-brand-gold" />
                  <h3 className="font-heading text-lg font-semibold">Market Insights</h3>
                  <p className="font-body text-sm text-muted-foreground">Data-driven property valuations</p>
                </div>
                <div className="space-y-2">
                  <Search className="w-8 h-8 text-brand-gold" />
                  <h3 className="font-heading text-lg font-semibold">Easy Search</h3>
                  <p className="font-body text-sm text-muted-foreground">Find exactly what you need</p>
                </div>
              </div>
            </div>
            <div className="relative h-[500px] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_6e5b1f29-9693-4de0-a8bc-fd5837d3df49/artifacts/lfjdxh16_KaufmannHouseRichardNeutraPalm-SpringsCaliforniaUS1947ccJulius-ShulmanJ.-PaulGettyTrust.GettyResearchInstitute_Los-Angeles2004.R.10_pages-96-97.jpg"
                alt="Modern luxury home"
                className="w-full h-full object-cover listing-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-brand-brown text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-6 max-w-2xl mx-auto">
            <p className="font-heading text-xs uppercase tracking-widest font-bold text-white/60">Get In Touch</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
              Ready to Find Your <span className="text-brand-gold">Dream Home</span>?
            </h2>
            <p className="font-body text-white/70">
              Contact us today and let our experts guide you through the process.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6">
              {siteSettings.company_phone && (
                <a href={`tel:${siteSettings.company_phone}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors" data-testid="landing-phone">
                  <Phone className="w-5 h-5 text-brand-gold" />
                  <span className="font-body">{siteSettings.company_phone}</span>
                </a>
              )}
              {siteSettings.company_email && (
                <a href={`mailto:${siteSettings.company_email}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors" data-testid="landing-email">
                  <Mail className="w-5 h-5 text-brand-gold" />
                  <span className="font-body">{siteSettings.company_email}</span>
                </a>
              )}
            </div>
            <div className="pt-8">
              <Link to="/properties">
                <Button className="rounded-none font-heading font-bold uppercase tracking-wide px-12 py-6 bg-brand-gold text-brand-brown hover:bg-brand-gold/90" data-testid="cta-browse-btn">
                  Browse Properties
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-brand-brown border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé Properties" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-heading text-lg font-bold text-white">Signé <span className="text-brand-gold">Properties</span></span>
          </div>
          <p className="font-body text-sm text-white/50">
            © 2025 Signé Properties. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp />
    </div>
  );
};

export default Landing;
